import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { axiosget } from "../../helpers/httprequest";
import { FiCheckCircle, FiXCircle, FiClock, FiArrowLeft, FiRefreshCw } from "react-icons/fi";

const PaymentCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("verifying"); // verifying, success, failed, error
    const [transaction, setTransaction] = useState(null);
    const [error, setError] = useState(null);

    // Get reference from URL params (different gateways use different param names)
    const reference = searchParams.get("reference") ||
        searchParams.get("trxref") ||
        searchParams.get("rrr") ||
        searchParams.get("ref");

    useEffect(() => {
        const verifyPayment = async () => {
            if (!reference) {
                setStatus("error");
                setError("No payment reference found");
                return;
            }

            try {
                const response = await axiosget(`ms_payments/verify/${reference}`);

                if (response?.data?.success) {
                    const data = response.data.data;
                    setTransaction(data);
                    setStatus(data.status === "success" ? "success" : "failed");
                } else {
                    setStatus("error");
                    setError(response?.data?.message || "Failed to verify payment");
                }
            } catch (err) {
                setStatus("error");
                setError(err?.message || "Failed to verify payment");
            }
        };

        verifyPayment();
    }, [reference]);

    const handleRetry = () => {
        setStatus("verifying");
        window.location.reload();
    };

    const renderContent = () => {
        switch (status) {
            case "verifying":
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 relative">
                            <div className="absolute inset-0 bg-primary-100 rounded-full animate-ping opacity-75"></div>
                            <div className="relative bg-primary-100 rounded-full w-20 h-20 flex items-center justify-center">
                                <FiClock className="w-10 h-10 text-primary-600 animate-pulse" />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Verifying Payment</h2>
                        <p className="text-neutral-600">Please wait while we confirm your payment...</p>
                    </motion.div>
                );

            case "success":
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                            <FiCheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-green-900 mb-2">Payment Successful!</h2>
                        <p className="text-neutral-600 mb-6">Your payment has been verified and confirmed.</p>

                        {transaction && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-neutral-500">Reference:</span>
                                        <p className="font-mono font-semibold text-neutral-800">{transaction.reference}</p>
                                    </div>
                                    <div>
                                        <span className="text-neutral-500">Amount:</span>
                                        <p className="font-semibold text-neutral-800">₦{transaction.amount?.toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Link
                            to="/app/pay"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            <FiArrowLeft className="w-4 h-4" />
                            Back to Payments
                        </Link>
                    </motion.div>
                );

            case "failed":
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                            <FiXCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-red-900 mb-2">Payment Failed</h2>
                        <p className="text-neutral-600 mb-6">
                            Your payment could not be verified. Please try again or contact support.
                        </p>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={handleRetry}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors"
                            >
                                <FiRefreshCw className="w-4 h-4" />
                                Retry Verification
                            </button>
                            <Link
                                to="/app/pay"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <FiArrowLeft className="w-4 h-4" />
                                Back to Payments
                            </Link>
                        </div>
                    </motion.div>
                );

            case "error":
            default:
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
                            <FiXCircle className="w-10 h-10 text-amber-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-amber-900 mb-2">Verification Error</h2>
                        <p className="text-neutral-600 mb-6">{error || "An unexpected error occurred"}</p>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={handleRetry}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors"
                            >
                                <FiRefreshCw className="w-4 h-4" />
                                Try Again
                            </button>
                            <Link
                                to="/app/pay"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <FiArrowLeft className="w-4 h-4" />
                                Back to Payments
                            </Link>
                        </div>
                    </motion.div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                {renderContent()}
            </div>
        </div>
    );
};

export default PaymentCallback;
